import environment from 'utils/environment';
import Storages from 'storages';
import StorageKeys from 'enum/storages.enum';

// Request timeout constant (10 seconds - suitable for TV environments)
const REQUEST_TIMEOUT = 10000;

/**
 * Utility function to add timeout to fetch requests
 * This is especially important for TV platforms where network behavior can be unpredictable
 */
function fetchWithTimeout(url, options = {}, timeout = REQUEST_TIMEOUT) {
  // Prefer aborting the underlying fetch (when supported) to avoid hanging requests on TV WebViews.
  if (typeof AbortController !== 'function') {
    return Promise.race([
      fetch(url, options),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Request timeout')), timeout)),
    ]);
  }

  const controller = new AbortController();
  let timedOut = false;

  const timeoutId = setTimeout(() => {
    timedOut = true;
    try {
      controller.abort(new Error('Request timeout'));
    } catch (_) {
      controller.abort();
    }
  }, timeout);

  return fetch(url, { ...options, signal: controller.signal })
    .catch((err) => {
      if (timedOut) {
        throw new Error('Request timeout');
      }
      throw err;
    })
    .finally(() => {
      clearTimeout(timeoutId);
    });
}

async function get(url) {
  var REGISTRATION_RES = Storages.MainStorage.getItem(StorageKeys.Keys.LICENSEAPIKEY);
  var API_Key = REGISTRATION_RES !== null ? REGISTRATION_RES.api_key : '';
  return new Promise(async (resolve, reject) => {
    let api_key = url.includes('?') ? '&' : '?';
    api_key += 'apikey=' + API_Key;
    const isXML = url.includes('xml');
    try {
      const response = await fetchWithTimeout(
        environment.getApiURL() + url + api_key,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            //'Accept-Language': Storages.MainStorage.getItem(StorageKeys.Keys.LANGUAGECODE),
          },
        },
        REQUEST_TIMEOUT
      );

      let result;
      if (isXML) {
        // Parse XML response
        const textResponse = await response.text();
        const parser = new DOMParser();
        result = parser.parseFromString(textResponse, 'application/xml');
      } else {
        // Parse JSON response
        result = await response.json().catch(() => undefined);
      }

      const res = {
        status: response.status,
        data: result,
      };

      if (response.status === 200) {
        resolve(res);
      } else {
        reject(res);
      }
    } catch (err) {
      console.error('HTTP Service Error:', err);
      reject({
        status: 0,
        data: { error: err.message || 'Network error' },
      });
    }
  });
}

function post(url, data) {
  var REGISTRATION_RES = Storages.MainStorage.getItem(StorageKeys.Keys.LICENSEAPIKEY);
  var API_Key = REGISTRATION_RES !== null ? REGISTRATION_RES.api_key : '';
  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetchWithTimeout(
        environment.getApiURL() + url + '?apikey=' + API_Key,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            //'Accept-Language': Storages.MainStorage.getItem(StorageKeys.Keys.LANGUAGECODE) || 'en',
          },
          body: JSON.stringify(data),
        },
        REQUEST_TIMEOUT
      );

      if (!response) {
        return;
      }

      const text = await response.text();
      let result;
      try {
        result = text ? JSON.parse(text) : text;
      } catch (error) {}

      const res = {
        status: response.status,
        data: result,
      };
      if (response.status >= 200 && response.status < 300) {
        resolve(res);
      } else {
        reject(res);
      }
    } catch (error) {
      console.error('HTTP Service Post Error:', error);
      reject({
        status: 0,
        data: { error: error.message || 'Network error' },
      });
    }
  });
}

function put(url, data) {
  var REGISTRATION_RES = Storages.MainStorage.getItem(StorageKeys.Keys.LICENSEAPIKEY);
  var API_Key = REGISTRATION_RES !== null ? REGISTRATION_RES.api_key : '';
  return new Promise((resolve, reject) => {
    let api_key = url.includes('?') ? '&' : '?';
    api_key += 'apikey=' + API_Key;

    fetchWithTimeout(
      environment.getApiURL() + url + api_key,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          //'Accept-Language': Storages.MainStorage.getItem(StorageKeys.Keys.LANGUAGECODE),
        },
        body: JSON.stringify(data),
      },
      REQUEST_TIMEOUT
    )
      .then((response) => {
        return response.json().then((result) => {
          const res = {
            status: response.status,
            data: result,
          };
          if (response.status === 201) {
            resolve(res);
          } else {
            result.status = response.status;
            reject(res);
          }
        });
      })
      .catch((error) => {
        reject(error);
      });
  });
}

function deleteReq(url, data) {
  var REGISTRATION_RES = Storages.MainStorage.getItem(StorageKeys.Keys.LICENSEAPIKEY);
  var API_Key = REGISTRATION_RES !== null ? REGISTRATION_RES.api_key : '';
  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetchWithTimeout(
        environment.getApiURL() + url + '?apikey=' + API_Key,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            //'Accept-Language': Storages.MainStorage.getItem(StorageKeys.Keys.LANGUAGECODE),
          },
          body: JSON.stringify(data),
        },
        REQUEST_TIMEOUT
      );

      const text = await response.text();
      let result;
      try {
        result = text ? JSON.parse(text) : text;
      } catch (error) {}

      const res = {
        status: response.status,
        data: result,
      };

      if (response.status === 201) {
        resolve(res);
      } else {
        reject(res);
      }
    } catch (err) {
      console.error('HTTP Service Delete Error:', err);
      reject({
        status: 0,
        data: { error: err.message || 'Network error' },
      });
    }
  });
}

function registration(url, data) {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetchWithTimeout(
        environment.getApiURL() + url,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            //'Accept-Language': Storages.MainStorage.getItem(StorageKeys.Keys.LANGUAGECODE),
          },
        },
        REQUEST_TIMEOUT
      );
      const result = await response.json();
      const res = {
        status: response.status,
        data: result,
      };

      if (response.status === 200) {
        resolve(res);
      } else {
        reject(res);
      }
    } catch (err) {
      console.error('HTTP Service Registration Error:', err);
      reject({
        status: 0,
        data: { error: err.message || 'Network error' },
      });
    }
  });
}

function getThirdParty(api) {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetchWithTimeout(api, {}, REQUEST_TIMEOUT);
      const records = await response.json();
      resolve(records);
    } catch (error) {
      console.error('Third Party API Error:', error);
      reject(error);
    }
  });
}

const obj = {
  get,
  post,
  put,
  deleteReq,
  registration,
  getThirdParty,
};

export default obj;
